import {Card, DataStatus, Segment} from 'platform/components';
import {Heading, Stack, Show, Space, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {always} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetPurchaseFunnelQuery, useGetSaleFunnelQuery} from '@dms/api/core';
import i18n from '@dms/i18n';
import {usePermissions} from '@dms/shared';

import {TestIdProps} from 'shared/web';

import {useRedirectFromOpportunities} from '../hooks/useRedirectFromOpportunities';
import {DashboardTileSize} from '../types/DashboardTileSize';
import {OwnerSegment} from '../types/OwnerSegment';
import {TypeSegment} from '../types/TypeSegment';
import {createOpportunitiesFunnelColumns} from '../utils/createOpportunitiesFunnelColumns';
import {OPPORTUNITIES_FUNNEL_SEGMENTS} from '../utils/opportunitiesFunnelSegments';
import {SEGMENT_MIN_WIDTH} from '../utils/segmentMinWidth';
import {OpportunitiesFunnelMetrics} from './OpportunitiesFunnelMetrics';

interface OpportunitiesFunnelProps extends TestIdProps {
  branchId: string | undefined;
  isFunnelInterestsEnabled: boolean;
  isFunnelBusinessCasesEnabled: boolean;
  size: DashboardTileSize;
}

export function OpportunitiesFunnel(props: OpportunitiesFunnelProps) {
  const [ownerSegment, setOwnerSegment] = useState<OwnerSegment>('total');
  const [typeSegment, setTypeSegment] = useState<TypeSegment>('selling');

  const [handleRedirectFromOpportunity, isRedirecting] = useRedirectFromOpportunities({
    branchId: props.branchId,
    ownerSegment,
    typeSegment,
  });

  const [canReadInterest] = usePermissions({
    permissionKeys: ['interestRead'],
    scopes: {
      interestRead: {
        // BE should handle participation scope regarding opportunities funnel
        participation: 'SKIP',
        // BE should handle branch scope regarding opportunities funnel
        branchId: 'SKIP',
      },
    },
  });

  const {
    data: purchaseFunnelData,
    isLoading: isPurchaseFunnelLoading,
    isError: isPurchaseFunnelError,
  } = useGetPurchaseFunnelQuery(
    {
      branchId: props.branchId,
      onlyMine: ownerSegment === 'my',
    },
    {skip: typeSegment === 'selling', refetchOnMountOrArgChange: true}
  );

  const {
    data: saleFunnelData,
    isLoading: isSaleFunnelLoading,
    isError: isSaleFunnelError,
  } = useGetSaleFunnelQuery(
    {
      branchId: props.branchId,
      onlyMine: ownerSegment === 'my',
    },
    {skip: typeSegment === 'buying', refetchOnMountOrArgChange: true}
  );

  const isLoading = match(typeSegment)
    .with('selling', always(isSaleFunnelLoading))
    .with('buying', always(isPurchaseFunnelLoading))
    .otherwise(always(false));

  const isError = match(typeSegment)
    .with('selling', always(isSaleFunnelError))
    .with('buying', always(isPurchaseFunnelError))
    .otherwise(always(false));

  const columns = match(typeSegment)
    .with(
      'selling',
      always(
        createOpportunitiesFunnelColumns(
          saleFunnelData,
          props.isFunnelInterestsEnabled,
          props.isFunnelBusinessCasesEnabled,
          canReadInterest,
          typeSegment
        )
      )
    )
    .with(
      'buying',
      always(
        createOpportunitiesFunnelColumns(
          purchaseFunnelData,
          props.isFunnelInterestsEnabled,
          props.isFunnelBusinessCasesEnabled,
          canReadInterest,
          typeSegment
        )
      )
    )
    .otherwise(always([]));

  return (
    <Card isFullHeight>
      <VStack spacing={4} height="100%">
        <Stack direction={['column', 'row']}>
          <Heading size={4}>{i18n.t('page.homepage.labels.currentOpportunities')}</Heading>
          <Show onTablet onNotebook onDesktop>
            <Space fillAvailable />
          </Show>
          <Stack direction={['column', 'row']} spacing={2}>
            <Segment
              value={ownerSegment}
              options={OPPORTUNITIES_FUNNEL_SEGMENTS.owner}
              onChange={(segment) => setOwnerSegment(segment)}
              minWidth={SEGMENT_MIN_WIDTH}
            />
            <Segment
              value={typeSegment}
              options={OPPORTUNITIES_FUNNEL_SEGMENTS.type}
              onChange={(segment) => setTypeSegment(segment)}
              minWidth={SEGMENT_MIN_WIDTH}
            />
          </Stack>
        </Stack>

        <DataStatus
          isError={isError}
          isEmpty={isNilOrEmpty(columns)}
          isLoading={isLoading}
          minHeight={56}
        >
          <OpportunitiesFunnelMetrics
            columns={columns}
            size={props.size}
            isRedirecting={isRedirecting}
            onSelect={handleRedirectFromOpportunity}
            data-testid={props['data-testid']}
          />
        </DataStatus>
      </VStack>
    </Card>
  );
}
